/**
 * ShareMenu — reusable "Share" dropdown with Copy Link + app share options.
 *
 * Renders a trigger (icon + optional label) that toggles a small popover with:
 *   - Copy Link        (clipboard, with local "Copied!" feedback)
 *   - WhatsApp         (wa.me share)
 *   - Facebook         (facebook sharer)
 *   - X / Twitter      (twitter intent)
 *   - Native Share     (only when navigator.share exists — the OS share sheet,
 *                       which itself has copy link + every installed app)
 *
 * After ANY option is chosen, `onShared?.()` is invoked (best-effort count
 * increment hook — the caller decides whether/when to hit the server) and the
 * dropdown closes. The count increment is intentionally left to the caller so
 * this component can be used on public pages where the share API is 403 for
 * anonymous users (the copy/share still succeeds client-side; the caller's
 * `onShared` is expected to swallow server errors silently).
 *
 * The dropdown is rendered through a portal (document.body) with `position:
 * fixed`, so it is NOT clipped by an `overflow-hidden` ancestor — the reels
 * player card clips every absolutely-positioned child, so an in-place dropdown
 * was getting cut in half on both laptop and mobile. The menu opens UPWARD
 * (the Share button lives in the bottom action rail) and aligns its right edge
 * to the trigger's right edge so it stays on screen.
 *
 * Closes on outside click, Escape, scroll/resize, or item select. No new
 * dependency.
 */
import { useEffect, useRef, useState, useCallback, useLayoutEffect } from 'react'
import { createPortal } from 'react-dom'
import { Share2, Copy, Check, Link as LinkIcon } from 'lucide-react'

interface ShareMenuProps {
  url: string
  title?: string
  text?: string
  onShared?: () => void
  /** Which side of the trigger the dropdown anchors to (only meaningful when
   *  there's room; the portal reflows to stay on screen anyway). */
  align?: 'left' | 'right'
  /** Optional label next to the trigger icon. Omit for icon-only. */
  label?: string
  /** Extra className for the trigger button. */
  triggerClassName?: string
}

const WhatsAppIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.884a9.86 9.86 0 001.51 5.26l-.999 3.648 3.978-1.037zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z"/>
  </svg>
)

const FacebookIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
  </svg>
)

const XIcon = ({ className }: { className?: string }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
  </svg>
)

const ShareMenu: React.FC<ShareMenuProps> = ({
  url,
  title,
  text,
  onShared,
  label,
  triggerClassName = '',
}) => {
  const [open, setOpen] = useState(false)
  const [copied, setCopied] = useState(false)
  // Fixed viewport coordinates for the portalled dropdown (computed from the
  // trigger's bounding rect when the menu opens).
  const [coords, setCoords] = useState<{ top: number; right: number } | null>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const menuRef = useRef<HTMLDivElement>(null)

  const canNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function'

  const close = useCallback(() => setOpen(false), [])

  // Position the dropdown each time it opens (and keep it placed while open).
  useLayoutEffect(() => {
    if (!open) return
    const place = () => {
      const rect = triggerRef.current?.getBoundingClientRect()
      if (rect) {
        // Right-align the menu's right edge to the trigger's right edge, and
        // open upward (the Share button sits in the bottom action rail). The
        // `translateY(-100%)` flips the menu above the `top` anchor point.
        setCoords({ top: rect.top, right: window.innerWidth - rect.right })
      }
    }
    place()
    // Reposition on resize; close on scroll (fixed positioning would otherwise
    // drift away from the trigger as the page scrolls).
    const onResize = () => place()
    const onScroll = () => close()
    window.addEventListener('resize', onResize)
    window.addEventListener('scroll', onScroll, true)
    return () => {
      window.removeEventListener('resize', onResize)
      window.removeEventListener('scroll', onScroll, true)
    }
  }, [open, close])

  // Outside click + Escape to close.
  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const target = e.target as Node
      if (triggerRef.current?.contains(target)) return
      if (menuRef.current?.contains(target)) return
      close()
    }
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close() }
    document.addEventListener('mousedown', onDoc)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      document.removeEventListener('keydown', onKey)
    }
  }, [open, close])

  const finish = useCallback(() => {
    onShared?.()
    close()
  }, [onShared, close])

  const copyLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url)
    } catch {
      // Fallback for browsers without async clipboard access.
      try {
        const ta = document.createElement('textarea')
        ta.value = url
        ta.style.position = 'fixed'
        ta.style.opacity = '0'
        document.body.appendChild(ta)
        ta.select()
        document.execCommand('copy')
        document.body.removeChild(ta)
      } catch { /* ignore — still report "copied" so the user can try manually */ }
    }
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
    finish()
  }, [url, finish])

  const openWindow = useCallback((href: string) => {
    window.open(href, '_blank', 'noopener,noreferrer,width=600,height=600')
    finish()
  }, [finish])

  const nativeShare = useCallback(async () => {
    try {
      await navigator.share({ title, text, url })
    } catch { /* user dismissed the sheet — no error UI */ }
    finish()
  }, [title, text, url, finish])

  const enc = encodeURIComponent(url)

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Share"
        className={triggerClassName}
      >
        <Share2 className="w-6 h-6" />
        {label && <span className="ml-1.5">{label}</span>}
      </button>

      {open && coords && createPortal(
        <div
          ref={menuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: coords.top,
            right: coords.right,
            transform: 'translateY(calc(-100% - 8px))',
            zIndex: 9999,
          }}
          className="w-44 rounded-xl bg-white shadow-lg border border-gray-100 py-1.5"
        >
          <button
            type="button"
            role="menuitem"
            onClick={copyLink}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4 text-gray-500" />}
            <span>{copied ? 'Copied!' : 'Copy Link'}</span>
          </button>

          <button
            type="button"
            role="menuitem"
            onClick={() => openWindow(`https://wa.me/?text=${enc}`)}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <WhatsAppIcon className="w-4 h-4 text-green-600" />
            <span>WhatsApp</span>
          </button>

          <button
            type="button"
            role="menuitem"
            onClick={() => openWindow(`https://www.facebook.com/sharer/sharer.php?u=${enc}`)}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <FacebookIcon className="w-4 h-4 text-blue-600" />
            <span>Facebook</span>
          </button>

          <button
            type="button"
            role="menuitem"
            onClick={() => openWindow(`https://twitter.com/intent/tweet?url=${enc}${text ? `&text=${encodeURIComponent(text)}` : ''}`)}
            className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
          >
            <XIcon className="w-4 h-4 text-gray-800" />
            <span>X / Twitter</span>
          </button>

          {canNativeShare && (
            <button
              type="button"
              role="menuitem"
              onClick={nativeShare}
              className="w-full flex items-center gap-3 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50"
            >
              <LinkIcon className="w-4 h-4 text-blue-600" />
              <span>More apps…</span>
            </button>
          )}
        </div>,
        document.body
      )}
    </>
  )
}

export default ShareMenu